import type { HTMLInputTypeAttribute, ReactElement } from "react";

import { describedBy, errorIdOf, helpIdOf } from "./described-by.js";

/**
 * 入力欄の部品。<b>どの画面でも同じ HTML にする</b>（クラス・属性・必須の印）。
 * 見た目の約束（docs/05 §11）を 2 通りに書き分けない。
 */

/** 欄に結び付かない断り。<b>文字として出す</b>（HTML にしない）。 */
export function FormSummary({ messages }: { readonly messages: readonly string[] }): ReactElement | null {
    if (messages.length === 0) {
        return null;
    }

    return (
        <div className="alert-danger mb-5" role="alert">
            <ul>
                {messages.map((message) => <li key={message}>{message}</li>)}
            </ul>
        </div>
    );
}

export interface TextFieldProps {
    readonly label: string;
    readonly name: string;
    readonly type?: HTMLInputTypeAttribute;
    readonly required?: boolean;
    readonly error?: string | undefined;
    readonly placeholder?: string;
    readonly autoComplete?: string;
    readonly autoFocus?: boolean;
    readonly inputMode?: "numeric" | "email" | "text";
    readonly maxLength?: number;
    /** 最初に入れておく値（編集画面の今の名前など）。 */
    readonly defaultValue?: string | undefined;
    /** 欄を囲む枠のクラス（下の余白）。 */
    readonly wrapperClassName?: string;
    /** 入力欄に足すクラス（確認コードの大きな文字など）。 */
    readonly inputClassName?: string;
    /** 欄の下に添える説明（「10 文字以上で入力してください。」）。画面の <c>form-help</c> と同じ位置に出す。 */
    readonly help?: string;
}

/** 見出し付きの 1 行の入力欄と、その断り。 */
export function TextField(props: TextFieldProps): ReactElement {
    const { label, name, error } = props;
    const invalid = error !== undefined;

    return (
        <div className={props.wrapperClassName ?? "mb-4"}>
            <label className="form-label" htmlFor={name}>
                {label}
                {props.required === true
                    ? <span className="ml-1 text-red-600" aria-hidden="true">*</span>
                    : null}
            </label>
            <input
                className={["form-input", props.inputClassName, invalid ? "is-invalid" : undefined]
                    .filter(Boolean).join(" ")}
                id={name}
                name={name}
                type={props.type ?? "text"}
                placeholder={props.placeholder}
                autoComplete={props.autoComplete}
                autoFocus={props.autoFocus}
                inputMode={props.inputMode}
                maxLength={props.maxLength}
                defaultValue={props.defaultValue}
                aria-invalid={invalid ? true : undefined}
                {...describedBy(name, { help: props.help !== undefined, invalid, required: props.required === true })}
            />
            {props.help === undefined ? null : <p className="form-help" id={helpIdOf(name)}>{props.help}</p>}
            <span
                className={`form-error ${invalid ? "field-validation-error" : "field-validation-valid"}`}
                id={errorIdOf(name)}
                data-valmsg-for={name}
            >
                {error}
            </span>
        </div>
    );
}
